import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Clock, Upload,
  FileText, XCircle, RefreshCw, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const humanizeKey = (k) => (k || '')
  .replace(/[_-]/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Documento';

const STATUS_META = {
  approved:    { label: 'Aprobado',          color: '#10b981', bg: '#dcfce7', Icon: CheckCircle2 },
  subsanation: { label: 'Requiere subsanación', color: '#f59e0b', bg: '#fef3c7', Icon: AlertTriangle },
  resubmitted: { label: 'En revisión',       color: '#3b82f6', bg: '#dbeafe', Icon: RefreshCw },
  expired:     { label: 'Plazo vencido',     color: '#ef4444', bg: '#fee2e2', Icon: XCircle },
};

const useCountdown = (deadlineIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!deadlineIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  if (!deadlineIso) return null;
  const target = new Date(deadlineIso).getTime();
  const diff = target - now;
  if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { expired: false, days, hours, minutes, seconds };
};

export default function MisNotificaciones() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/evaluaciones/mis-notificaciones`, { headers });
      setNotificaciones(data.notificaciones || []);
    } catch {
      toast.error('Error al cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/evaluaciones/${id}/marcar-leida`, {}, { headers });
      setNotificaciones((prev) => prev.map(n => n.id === id ? { ...n, leido_por_postulante: true } : n));
    } catch { /* ignore */ }
  };

  const handleUpload = async (id, file) => {
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'xls', 'xlsx'].includes(ext)) {
      toast.error('Solo se permiten archivos PDF o Excel');
      return;
    }
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/evaluaciones/${id}/resubmit`, fd, { headers });
      toast.success('Documento re-enviado. Queda en revisión.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo re-enviar el documento');
    } finally {
      setUploadingId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { total: notificaciones.length, subsanation: 0, expired: 0, approved: 0 };
    notificaciones.forEach((n) => {
      if (n.expired) c.expired++;
      else if (n.estado === 'subsanation') c.subsanation++;
      else if (n.estado === 'approved') c.approved++;
    });
    return c;
  }, [notificaciones]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              data-testid="btn-back-notif"
              className="text-white hover:bg-slate-700 p-2 rounded"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-white">Mis Notificaciones</h1>
              <p className="text-sm text-slate-300">Decisiones del evaluador sobre tus documentos</p>
            </div>
          </div>
          <div className="text-slate-300 text-sm">
            {user?.first_name} {user?.first_lastname}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total" value={counts.total} accent="#0f172a" />
          <StatCard label="Aprobados" value={counts.approved} accent="#10b981" />
          <StatCard label="Subsanaciones" value={counts.subsanation} accent="#f59e0b" />
          <StatCard label="Vencidas" value={counts.expired} accent="#ef4444" />
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-12">Cargando…</div>
        ) : notificaciones.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="notif-empty">
            <FileText size={40} className="mx-auto text-slate-300 mb-3" />
            <div className="text-slate-600 font-medium">No tienes notificaciones aún</div>
            <div className="text-slate-400 text-sm mt-1">Cuando el evaluador revise tus documentos aparecerán aquí.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notif-list">
            {notificaciones.map((n) => (
              <NotificationCard
                key={n.id}
                notif={n}
                onMarkRead={() => markAsRead(n.id)}
                onUpload={(file) => handleUpload(n.id, file)}
                uploading={uploadingId === n.id}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white rounded-lg border-l-4 shadow-sm px-4 py-3" style={{ borderColor: accent }}>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

const NotificationCard = ({ notif, onMarkRead, onUpload, uploading }) => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const isExpired = notif.expired || notif.estado === 'expired';
  const statusKey = isExpired ? 'expired' : notif.estado;
  const meta = STATUS_META[statusKey] || STATUS_META.approved;
  const Icon = meta.Icon;
  const countdown = useCountdown(notif.estado === 'subsanation' && !isExpired ? notif.fecha_limite : null);

  const openFile = (url) => {
    window.open(`${backend}${url}`, '_blank');
  };

  return (
    <div
      className={`bg-white rounded-lg border-l-4 shadow-sm p-5 ${!notif.leido_por_postulante ? 'ring-1 ring-blue-100' : ''}`}
      style={{ borderColor: meta.color }}
      data-testid={`notif-card-${notif.id}`}
      onMouseEnter={() => !notif.leido_por_postulante && onMarkRead()}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[250px]">
          <div style={{ background: meta.bg, padding: 10, borderRadius: 8 }}>
            <Icon size={22} color={meta.color} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">{humanizeKey(notif.documento_nombre)}</span>
              <span style={{ background: meta.bg, color: meta.color, padding: '0.15rem 0.6rem', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                {meta.label}
              </span>
              {!notif.leido_por_postulante && (
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" title="Sin leer" />
              )}
            </div>
            {notif.convocatoria_codigo && (
              <div className="text-xs text-slate-500 mt-1">
                {notif.convocatoria_codigo} — {notif.convocatoria_nombre}
              </div>
            )}
            {notif.observacion && (
              <div className="mt-2 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap">
                <div className="text-xs uppercase text-slate-500 mb-1 font-medium">Observación del evaluador</div>
                {notif.observacion}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-2">
              Emitido: {new Date(notif.created_at).toLocaleString('es-CO')}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2 min-w-[220px]">
          {notif.documento_url_original && (
            <button
              onClick={() => openFile(notif.documento_url_original)}
              className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              <Eye size={12} /> Ver documento original
            </button>
          )}

          {notif.estado === 'subsanation' && !isExpired && countdown && (
            <>
              <CountdownBadge countdown={countdown} />
              <label
                htmlFor={`upload-${notif.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-medium cursor-pointer ${
                  uploading ? 'bg-slate-300 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                data-testid={`btn-resubmit-${notif.id}`}
              >
                <Upload size={14} /> {uploading ? 'Subiendo…' : 'Subir nuevo documento'}
              </label>
              <input
                id={`upload-${notif.id}`}
                type="file"
                accept=".pdf,.xls,.xlsx"
                style={{ display: 'none' }}
                onChange={(e) => onUpload(e.target.files?.[0])}
                disabled={uploading}
              />
            </>
          )}

          {notif.estado === 'resubmitted' && notif.documento_url_nuevo && (
            <div className="text-xs text-slate-500 flex flex-col items-end gap-1">
              <span className="text-blue-700">Nuevo documento enviado</span>
              <button onClick={() => openFile(notif.documento_url_nuevo)} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                <Eye size={12} /> Ver nueva versión
              </button>
            </div>
          )}

          {isExpired && (
            <div className="text-xs text-red-700 text-right max-w-[220px]">
              <div className="font-semibold flex items-center gap-1 justify-end">
                <XCircle size={14} /> Plazo vencido
              </div>
              <div className="mt-1">Tu postulación ha sido rechazada por vencimiento de subsanación.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CountdownBadge = ({ countdown }) => {
  const urgent = countdown.days === 0 && countdown.hours < 12;
  const color = urgent ? '#dc2626' : '#f59e0b';
  const bg = urgent ? '#fee2e2' : '#fef3c7';
  const label = urgent ? 'Urgente — quedan' : 'Plazo para subsanar';
  return (
    <div
      style={{ background: bg, color, padding: '0.5rem 0.85rem', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, textAlign: 'right', minWidth: 200 }}
      data-testid="countdown-badge"
    >
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.75, marginBottom: 2 }}>
        <Clock size={11} style={{ display: 'inline', marginRight: 4 }} />
        {label}
      </div>
      <div style={{ fontSize: '1.05rem', fontFamily: 'monospace' }}>
        {String(countdown.days).padStart(2, '0')}d {String(countdown.hours).padStart(2, '0')}h {String(countdown.minutes).padStart(2, '0')}m {String(countdown.seconds).padStart(2, '0')}s
      </div>
    </div>
  );
};
